import { useEffect, useRef } from 'react'
import { Search, X } from 'lucide-react'
import { Autocomplete, Avatar, IconButton } from '@/components/play'
import './athlete-search.css'

/** An athlete the search matched, as its list shows them. */
export interface AthleteSearchHit {
  id: string
  name: string
  bib: string | null
  photoUrl: string | null
  /** Their race's name and colour, when they have one. */
  race: { name: string; color: string } | null
  /** How old their position is ("3 min ago"), or null when the map doesn't draw them (no position yet, or one outside
   *  the play world's area): those are listed but can't be picked. */
  age: string | null
}

interface Props {
  /** What the viewer typed, or null while the search is closed. */
  query: string | null
  onQueryChange: (query: string | null) => void
  /** The athletes it lists: those on the map first, then those it doesn't draw. */
  hits: readonly AthleteSearchHit[]
  /** How many match in all, the listed ones included, and how many of those the map draws. */
  total: number
  onMap: number
  /** A listed athlete chosen with a press or Enter: show them and open their card. */
  onPick: (id: string) => void
  /** The compact button, beside the play world's compact Satellite switch. */
  compact?: boolean
}

/** The list's foot: how many match and how many of those are on the map, or how many it leaves out. */
function statusText(listed: number, total: number, onMap: number): string {
  if (listed < total) return `${listed} of ${total}: keep typing to narrow it down`
  const athletes = total === 1 ? '1 athlete' : `${total} athletes`
  return onMap === total ? athletes : `${athletes}, ${onMap} on the map`
}

/**
 * Finding athletes on the map (docs/features/athletes-and-trackers.md, Map): a search button with the map's controls
 * that opens a small field in place. The map answers as the viewer types, and a list under the field names who matched,
 * with their photo, bib, race and how fresh their position is, and how many match at its foot. The first is highlighted: Enter or a press shows that
 * athlete and opens their card, and what was typed stays so another can be picked. Escape or Close puts it all away,
 * and focus goes back to the button.
 */
export function AthleteSearch({ query, onQueryChange, hits, total, onMap, onPick, compact = false }: Props) {
  const button = useRef<HTMLButtonElement>(null)
  const bar = useRef<HTMLDivElement>(null)
  const wasOpen = useRef(false)
  const open = query !== null

  useEffect(() => {
    if (wasOpen.current && !open) button.current?.focus()
    wasOpen.current = open
  }, [open])

  if (!open) {
    return (
      <IconButton ref={button} label="Find an athlete" size={compact ? 'compact' : undefined} elevated onClick={() => onQueryChange('')}>
        <Search />
      </IconButton>
    )
  }

  return (
    <div ref={bar} className="lv-athlete-search" role="search">
      <Autocomplete
        label="Find an athlete"
        hideLabel
        size="compact"
        icon={<Search />}
        placeholder="Bib or name"
        autoFocus
        value={query}
        onValueChange={onQueryChange}
        items={hits}
        itemLabel={(hit) => hit.name}
        itemKey={(hit) => hit.id}
        itemDisabled={(hit) => hit.age === null}
        keepValue
        onItemChoose={(hit) => onPick(hit.id)}
        renderItem={(hit) => <HitRow hit={hit} />}
        empty="No athlete matches"
        status={statusText(hits.length, total, onMap)}
        anchor={bar}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          // Escape puts the whole search away, rather than only clearing the field as an autocomplete's would.
          event.preventBaseUIHandler()
          event.preventDefault()
          event.stopPropagation()
          onQueryChange(null)
        }}
      />
      <IconButton label="Close search" size="compact" variant="ghost" onClick={() => onQueryChange(null)}>
        <X />
      </IconButton>
    </div>
  )
}

/** One athlete in the list: photo or initials, name, then bib and race, and on the right how old their position is. */
function HitRow({ hit }: { hit: AthleteSearchHit }) {
  return (
    <>
      <Avatar name={hit.name} src={hit.photoUrl} size="sm" tone="auto" toneKey={hit.id} />
      <span className="lv-athlete-search__hit">
        <span className="lv-athlete-search__name">{hit.name}</span>
        {(hit.bib || hit.race) && (
          <span className="lv-athlete-search__meta">
            {hit.bib && <span>Bib {hit.bib}</span>}
            {hit.race && (
              <span className="lv-athlete-search__race">
                <span className="lv-athlete-search__swatch" style={{ background: hit.race.color }} aria-hidden />
                {hit.race.name}
              </span>
            )}
          </span>
        )}
      </span>
      <span className="lv-athlete-search__age">{hit.age ?? 'Not on the map'}</span>
    </>
  )
}
